import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { UserCheck, Search, Clock, CheckCircle, Plus } from 'lucide-react';
import api from '../../api/axios';
import Drawer from '../../components/common/Drawer/Drawer';
import styles from './Attendance.module.css';

const Attendance = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', role: 'Maid', phone: '', passId: '', flatNumber: '' });

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const date = new Date().toISOString().split('T')[0];
      const [workersRes, attendanceRes] = await Promise.all([
        api.get('/security/workers'),
        api.get(`/security/worker-attendance?date=${date}`)
      ]);
      
      const workers = workersRes.data;
      const attendances = attendanceRes.data;

      const mergedList = workers.map(worker => {
        const att = attendances.find(a => a.worker && a.worker._id === worker._id);
        let status = 'Absent';
        let timeIn = '-';
        let timeOut = '-';
        
        if (att) {
          timeIn = att.checkInTime ? new Date(att.checkInTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-';
          timeOut = att.checkOutTime ? new Date(att.checkOutTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-';
          if (att.checkOutTime) {
            status = 'Completed';
          } else if (att.checkInTime) {
            status = 'Present';
          }
        }

        return {
          id: worker._id,
          passId: worker.passId || '-',
          name: worker.name,
          role: worker.role,
          phone: worker.phone,
          flatNumber: worker.flatNumber || '-',
          status,
          timeIn,
          timeOut
        };
      });

      setStaffList(mergedList);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load attendance');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, []);

  const handleAction = async (id, action) => {
    try {
      if (action === 'Present') {
        await api.post(`/security/worker-attendance/${id}/checkin`);
        toast.success('Check-in recorded!');
      } else if (action === 'Completed') {
        await api.post(`/security/worker-attendance/${id}/checkout`);
        toast.success('Check-out recorded!');
      }
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update attendance');
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    try {
      await api.post('/security/workers', formData);
      toast.success('Worker added successfully!');
      setIsDrawerOpen(false);
      setFormData({ name: '', role: 'Maid', phone: '', passId: '', flatNumber: '' });
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add worker');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Worker Attendance</h1>
          <p>Mark attendance for society staff, guards, and maids</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <Plus size={16} /> Add Worker
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search staff name or role..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Pass ID</th>
                <th>Name & Role</th>
                <th>Flat No.</th>
                <th>Phone</th>
                <th>Time In</th>
                <th>Time Out</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : staffList.map(s => (
                <tr key={s.id}>
                  <td><strong>{s.passId}</strong></td>
                  <td>
                    <div className={styles.staffInfo}>
                      <div className={styles.avatarSmall}>{s.name.charAt(0)}</div>
                      <div className={styles.details}>
                        <strong>{s.name}</strong>
                        <span>{s.role}</span>
                      </div>
                    </div>
                  </td>
                  <td>{s.flatNumber}</td>
                  <td>{s.phone || '-'}</td>
                  <td>{s.timeIn}</td>
                  <td>{s.timeOut}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[s.status.toLowerCase()]}`}>
                      {s.status}
                    </span>
                  </td>
                  <td>
                    {s.status === 'Absent' && (
                      <button className={styles.btnIn} onClick={() => handleAction(s.id, 'Present')}>
                        <UserCheck size={14} /> Mark In
                      </button>
                    )}
                    {s.status === 'Present' && (
                      <button className={styles.btnOut} onClick={() => handleAction(s.id, 'Completed')}>
                        <Clock size={14} /> Mark Out
                      </button>
                    )}
                    {s.status === 'Completed' && (
                      <span className={styles.textSuccess}><CheckCircle size={16} /> Done</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? <div style={{padding: '16px'}}>Loading...</div> : staffList.map(s => (
            <div key={s.id} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <div className={styles.staffInfo}>
                  <div className={styles.avatarSmall}>{s.name.charAt(0)}</div>
                  <div className={styles.details}>
                    <strong>{s.name}</strong>
                    <span>{s.role}</span>
                  </div>
                </div>
                <span className={`${styles.badgeStatus} ${styles[s.status.toLowerCase()]}`}>
                  {s.status}
                </span>
              </div>
              <div className={styles.mobileCardBody}>
                <div className={styles.metaRow}>
                  <span>Pass: <strong>{s.passId}</strong></span>
                  <span>Flat: <strong>{s.flatNumber}</strong></span>
                </div>
                <div className={styles.metaRow}>
                  <span>In: <strong>{s.timeIn}</strong></span>
                  <span>Out: <strong>{s.timeOut}</strong></span>
                </div>
                {s.phone && s.phone !== '-' && (
                  <div className={styles.metaRow}>
                    <span>Phone: <strong>{s.phone}</strong></span>
                  </div>
                )}
                
                <div className={styles.actionRow}>
                    {s.status === 'Absent' && (
                      <button className={styles.btnIn} style={{width: '100%', justifyContent: 'center'}} onClick={() => handleAction(s.id, 'Present')}>
                        <UserCheck size={14} /> Mark In
                      </button>
                    )}
                    {s.status === 'Present' && (
                      <button className={styles.btnOut} style={{width: '100%', justifyContent: 'center'}} onClick={() => handleAction(s.id, 'Completed')}>
                        <Clock size={14} /> Mark Out
                      </button>
                    )}
                    {s.status === 'Completed' && (
                      <span className={styles.textSuccess} style={{width: '100%', justifyContent: 'center'}}><CheckCircle size={16} /> Done</span>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Add New Worker">
        <form className="drawerForm" onSubmit={handleAddWorker}>
          <div className="formGroup">
            <label>Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="Full Name" />
          </div>
          <div className="formGroup">
            <label>Role</label>
            <select name="role" value={formData.role} onChange={handleChange}>
              <option value="Maid">Maid</option>
              <option value="Plumber">Plumber</option>
              <option value="Electrician">Electrician</option>
              <option value="Security">Security Guard</option>
              <option value="Housekeeping">Housekeeping</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Phone (Optional)</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="10-digit number" />
          </div>
          <div className="formGroup">
            <label>Pass ID (Optional)</label>
            <input name="passId" value={formData.passId} onChange={handleChange} placeholder="e.g. ST-001" />
          </div>
          <div className="formGroup">
            <label>Visiting Flat/House (Optional)</label>
            <input name="flatNumber" value={formData.flatNumber} onChange={handleChange} placeholder="e.g. A-101 (Leave empty for Society Staff)" />
          </div>
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Save Worker</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Attendance;
